
import { LocationsContext } from "../context/LocationsContext";
import { useLocations } from "hooks/useLocations";
import { getTimezoneFromLocalStorage } from "helpers/datetime_helper";
import { getCountryByTimezone } from "helpers/country_helper";
import { useState, useEffect, useMemo } from "react";

const LocationsProvider = ({ children }: { children: React.ReactNode }) => {
  // Must define selectedCommune state BEFORE calling useLocations
  const [selectedCommune, setSelectedCommune] = useState<string | null>(null);

  const timezone = getTimezoneFromLocalStorage();
  const country = getCountryByTimezone(timezone);

  // Pass selectedCommune to hook
  const { regions, communes, selectedRegion, setSelectedRegion, isLoadingRegions, isLoadingCommunes, error, communeCoordinates, communeGeoError } = useLocations(country, selectedCommune);

  // Reset downstream filters when selectedRegion changes
  useEffect(() => {
    console.log("LocationsProvider: Region changed, resetting downstream filters.");
    setSelectedCommune(null);
    setSelectedCouriers([]);
    setSelectedPolygonType("all"); // Reset to "all" consistent with other resets
    setSelectedPolygon(null);
  }, [selectedRegion]);

  const [selectedCouriers, setSelectedCouriers] = useState<any[]>([]);
  const [selectedPolygonType, setSelectedPolygonType] = useState<string | null>(null);
  const [selectedPolygon, setSelectedPolygon] = useState<any | null>(null);

  // "allPolygons" holds the raw fetched data (Master List)
  const [allPolygons, setAllPolygons] = useState<any[]>([]);
  const [isLoadingPolygons, setIsLoadingPolygons] = useState(false);

  const [polygonsRefreshTrigger, setPolygonsRefreshTrigger] = useState<number>(0);

  // Reset downstream filters when selectedCommune changes
  useEffect(() => {
    if (selectedCommune) {
      setSelectedPolygonType("all"); // Default to "all"
      setSelectedCouriers([]); // Reset couriers
      setSelectedPolygon(null); // Reset specific polygon
    } else {
      // If commune is cleared, maybe clear these too?
      setSelectedPolygonType(null);
      setSelectedCouriers([]);
      setSelectedPolygon(null);
    }
  }, [selectedCommune]);

  // Reset selectedPolygon when selectedPolygonType changes - MOVED TO HANDLER to avoid loops
  // useEffect removed


  // Compute filtered polygons (Client-Side filtering)
  // This ensures BOTH the map and the filters see the filtered list
  const displayedPolygons = useMemo(() => {
    // If a specific polygon is selected, ONLY show that one (and ensure it matches filters if needed, or override filters?)
    // Usually, selecting a specific item overrides general filters.
    if (selectedPolygon) {
       return [selectedPolygon];
    }

    let filtered = allPolygons || [];

    if (selectedCouriers && selectedCouriers.length > 0) {
      // Get array of selected courier IDs
      const selectedIds = selectedCouriers.map(c => c.id);
      
      console.log("Filtering by couriers:", selectedIds);

      // Filter polygons that match ANY of the selected courier IDs
      // polygon.coverageIds is an array of courier IDs associated with the polygon
      // DEBUG: Log sample polygon to check structure
      if (filtered.length > 0) console.log("Sample Polygon for filtering:", filtered[0]);

      filtered = filtered.filter((poly: any) => {
        // We filter by 'courierIds' for selected couriers
        if (!poly.courierIds) return false;
        // Check if overlaps
        return poly.courierIds.some((id: string) => selectedIds.includes(id));
      });
      console.log("Filtered count by couriers:", filtered.length);
    }

    if (selectedPolygonType && selectedPolygonType !== "all") {
      filtered = filtered.filter((poly: any) => poly.type?.toLowerCase() === selectedPolygonType.toLowerCase());
    }

    return filtered;
  }, [allPolygons, selectedCouriers, selectedPolygonType, selectedPolygon]);

  // Debug selectedPolygon changes
  useEffect(() => {
    // console.log("LocationsProvider: selectedPolygon changed:", selectedPolygon?.name);
  }, [selectedPolygon]);

  return (
    <LocationsContext.Provider
      value={{
        regions,
        communes,
        selectedRegion,
        setSelectedRegion,
        selectedCommune,
        setSelectedCommune,
        selectedCouriers,
        setSelectedCouriers,
        selectedPolygonType,
        setSelectedPolygonType,
        selectedPolygon,
        setSelectedPolygon,
        displayedPolygons, // Expose FILTERED list
        setDisplayedPolygons: setAllPolygons, // Consumers update the MASTER list
        allPolygons, // Expose MASTER list
        isLoadingRegions,
        isLoadingCommunes,
        isLoadingPolygons,
        setIsLoadingPolygons,
        error,
        country,
        refreshPolygons: () => setPolygonsRefreshTrigger(prev => prev + 1),
        polygonsRefreshTrigger: polygonsRefreshTrigger,
        communeCoordinates, // Expose full coordinates object (center, area, extras)
        communeGeoError // Expose error state
      }}
    >
      {children}
    </LocationsContext.Provider>
  );
};

export default LocationsProvider;
